/**
 * React Query hooks for the agents endpoints (RLY-024).
 *
 * Standing-order [25] + Sprint-04 retro [PROCESS]: the `useQuery` generic types
 * are declared **explicitly** before the client method is implemented. The
 * companion `*-shape.test.ts` files ship `expectTypeOf<X>().toEqualTypeOf<Y>()`
 * assertions that lock the inferred shapes against drift. Reference pattern:
 * `packages/secrets/src/secrets-fetch-shape.test.ts` (RLY-226).
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AgentDetailResponse,
  AgentListResponse,
  AgentToolPackagesResponse,
  AgentVersionListResponse,
  ApiError,
  CreateAgentRequest,
  CreateAgentResponse,
  TriggerAssignment,
  TriggerListResponse,
  UpdateAgentRequest,
  UpdateAgentResponse,
  UpdateAgentToolPackagesRequest,
  UpsertTriggerRequest,
} from "@/lib/api-client";
import {
  createAgent,
  fetchAgent,
  fetchAgentToolPackages,
  fetchAgents,
  fetchAgentVersions,
  fetchTriggers,
  updateAgent,
  updateAgentToolPackages,
  upsertTrigger,
} from "@/lib/api-client";

/** Query-key factory — keeps cache invalidation surface tight + greppable. */
export const agentsKeys = {
  all: ["agents"] as const,
  list: (params: { cursor?: string; limit?: number } = {}) => ["agents", "list", params] as const,
  detail: (agentId: string) => ["agents", "detail", agentId] as const,
  versions: (agentId: string) => ["agents", "versions", agentId] as const,
  toolPackages: (agentId: string) => ["agents", "tool-packages", agentId] as const,
} as const;

export type AgentsListQueryKey = ReturnType<typeof agentsKeys.list>;
export type AgentDetailQueryKey = ReturnType<typeof agentsKeys.detail>;
export type AgentVersionsQueryKey = ReturnType<typeof agentsKeys.versions>;

// ─── List ─────────────────────────────────────────────────────────────────

export type AgentToolPackagesQueryKey = ReturnType<typeof agentsKeys.toolPackages>;

export type UseAgentsListResult = UseQueryResult<AgentListResponse, ApiError | Error>;

export function useAgentsList(
  params: { cursor?: string; limit?: number } = {},
): UseAgentsListResult {
  const { getToken } = useAuth();
  return useQuery<AgentListResponse, ApiError | Error, AgentListResponse, AgentsListQueryKey>({
    queryKey: agentsKeys.list(params),
    queryFn: () =>
      fetchAgents({
        getToken: () => getToken(),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
        ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
      }),
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────

export type UseAgentDetailResult = UseQueryResult<AgentDetailResponse, ApiError | Error>;

export function useAgentDetail(agentId: string | undefined): UseAgentDetailResult {
  const { getToken } = useAuth();
  return useQuery<AgentDetailResponse, ApiError | Error, AgentDetailResponse, AgentDetailQueryKey>({
    queryKey: agentsKeys.detail(agentId ?? ""),
    queryFn: () => {
      if (!agentId) throw new Error("agentId is required");
      return fetchAgent(agentId, { getToken: () => getToken() });
    },
    enabled: Boolean(agentId),
  });
}

// ─── Versions ─────────────────────────────────────────────────────────────

export type UseAgentVersionsResult = UseQueryResult<AgentVersionListResponse, ApiError | Error>;

export function useAgentVersions(agentId: string | undefined): UseAgentVersionsResult {
  const { getToken } = useAuth();
  return useQuery<
    AgentVersionListResponse,
    ApiError | Error,
    AgentVersionListResponse,
    AgentVersionsQueryKey
  >({
    queryKey: agentsKeys.versions(agentId ?? ""),
    queryFn: () => {
      if (!agentId) throw new Error("agentId is required");
      return fetchAgentVersions(agentId, { getToken: () => getToken() });
    },
    enabled: Boolean(agentId),
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export type UseCreateAgentResult = UseMutationResult<
  CreateAgentResponse,
  ApiError | Error,
  CreateAgentRequest
>;

export function useCreateAgent(): UseCreateAgentResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<CreateAgentResponse, ApiError | Error, CreateAgentRequest>({
    mutationFn: (body) => createAgent(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentsKeys.all });
    },
  });
}

export type UseUpdateAgentResult = UseMutationResult<
  UpdateAgentResponse,
  ApiError | Error,
  { agentId: string; body: UpdateAgentRequest }
>;

export function useUpdateAgent(): UseUpdateAgentResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    UpdateAgentResponse,
    ApiError | Error,
    { agentId: string; body: UpdateAgentRequest }
  >({
    mutationFn: ({ agentId, body }) => updateAgent(agentId, body, { getToken: () => getToken() }),
    onSuccess: (_data, { agentId }) => {
      void qc.invalidateQueries({ queryKey: agentsKeys.detail(agentId) });
      void qc.invalidateQueries({ queryKey: agentsKeys.versions(agentId) });
      void qc.invalidateQueries({ queryKey: agentsKeys.all });
    },
  });
}

// ─── Tool Packages (P2.5-B) ───────────────────────────────────────────────────

export type UseAgentToolPackagesResult = UseQueryResult<
  AgentToolPackagesResponse,
  ApiError | Error
>;

/** Fetch enabled + available tool packages for an agent. */
export function useAgentToolPackages(
  agentId: string | undefined,
): UseAgentToolPackagesResult {
  const { getToken } = useAuth();
  return useQuery<
    AgentToolPackagesResponse,
    ApiError | Error,
    AgentToolPackagesResponse,
    AgentToolPackagesQueryKey
  >({
    queryKey: agentsKeys.toolPackages(agentId ?? ""),
    queryFn: () => {
      if (!agentId) throw new Error("agentId is required");
      return fetchAgentToolPackages(agentId, { getToken: () => getToken() });
    },
    enabled: Boolean(agentId),
  });
}

export type UseUpdateAgentToolPackagesResult = UseMutationResult<
  AgentToolPackagesResponse,
  ApiError | Error,
  { agentId: string; body: UpdateAgentToolPackagesRequest }
>;

/** Update the enabled tool packages for an agent. */
export function useUpdateAgentToolPackages(): UseUpdateAgentToolPackagesResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    AgentToolPackagesResponse,
    ApiError | Error,
    { agentId: string; body: UpdateAgentToolPackagesRequest }
  >({
    mutationFn: ({ agentId, body }) =>
      updateAgentToolPackages(agentId, body, { getToken: () => getToken() }),
    onSuccess: (_data, { agentId }) => {
      void qc.invalidateQueries({ queryKey: agentsKeys.toolPackages(agentId) });
    },
  });
}

// ─── Trigger assignments (P2.5-C) ────────────────────────────────────────────

export const triggersKeys = {
  all: ["triggers"] as const,
  list: () => ["triggers", "list"] as const,
} as const;

export type TriggersListQueryKey = ReturnType<typeof triggersKeys.list>;

export type UseTriggersResult = UseQueryResult<TriggerListResponse, ApiError | Error>;

/** Fetch all trigger assignments for the active tenant. */
export function useTriggers(): UseTriggersResult {
  const { getToken } = useAuth();
  return useQuery<TriggerListResponse, ApiError | Error, TriggerListResponse, TriggersListQueryKey>(
    {
      queryKey: triggersKeys.list(),
      queryFn: () => fetchTriggers({ getToken: () => getToken() }),
    },
  );
}

export type UseUpsertTriggerResult = UseMutationResult<
  TriggerAssignment,
  ApiError | Error,
  { triggerType: string; body: UpsertTriggerRequest }
>;

/** Assign or unassign an agent to a trigger type. */
export function useUpsertTrigger(): UseUpsertTriggerResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    TriggerAssignment,
    ApiError | Error,
    { triggerType: string; body: UpsertTriggerRequest }
  >({
    mutationFn: ({ triggerType, body }) =>
      upsertTrigger(triggerType, body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: triggersKeys.all });
    },
  });
}
